import { useState, useRef, useEffect } from "react";
import { useBooking } from "../../../store/bookingStore";
import { parseLocalDateString } from "../../../util/parseLocalDate";
import {
    getBusyIntervalItems,
    getAvailabilityRangesForDate,
    isBookingRangeAvailable,
} from "../../../util/studioAvailability";
import ErrorMessage from "../../StudioErstellen/ErrorMessage";

import classes from "./TimePicker.module.css"

export const TimePicker = ({ nextSlide, bookingStudioData }) => {
    const { bookingData, updateBookingData } = useBooking();
    const initialEndTime = bookingData?.endTime === "00:00" ? "24:00" : bookingData?.endTime;
    const [selectedTime, setSelectedTime] = useState(bookingData?.startTime || "09:00");
    const [selectedEndTime, setSelectedEndTime] = useState(initialEndTime || "12:00");
    const [errorMessage, setErrorMessage] = useState("");
    const startScrollerRef = useRef(null);
    const endScrollerRef = useRef(null);
    const startScrollTimeoutRef = useRef(null);
    const endScrollTimeoutRef = useRef(null);
    const isStartSnappingRef = useRef(false);
    const isEndSnappingRef = useRef(false);
    const hasSyncedEndScrollRef = useRef(false);
    const minBookingDuration = bookingStudioData?.minNumberOfHours || 2;
    
    // Format date to German locale
    const formatDate = (dateString) => {
        const date = parseLocalDateString(dateString);
        if (!date) return '';
        return date.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Get available time ranges for the selected day
    const getAvailableTimeRanges = () => {
        if (!bookingData.date) return [];
        return getAvailabilityRangesForDate(bookingData.date, bookingStudioData);
    };

    // Improved generateTimeSlots from Booking.jsx
    const generateTimeSlots = () => {
        if (!bookingData.date) return [];
        
        const timeRanges = getAvailableTimeRanges();
        let allSlots = [];
        
        // Process each time range and collect all valid slots
        timeRanges.forEach(range => {
            const { start, end } = range;
            
            // Parse the start and end times
            const startDate = new Date(`2000-01-01T${start}`);
            const endDate = new Date(`2000-01-01T${end === "00:00" || end === "24:00" ? "24:00" : end}`);
            
            // Calculate the latest possible hour considering minimum booking duration
            const latestStartTime = new Date(endDate);
            latestStartTime.setHours(latestStartTime.getHours() - minBookingDuration);
            
            // Skip this range if it's too short for minimum booking duration
            if (startDate > latestStartTime) {
                return;
            }
            
            // Generate time slots from the start time up to the latest possible start time (inclusive)
            let current = new Date(startDate);
            while (current <= latestStartTime) {
                const timeString = current.getHours() === 24 ? "24:00" : 
                                 `${current.getHours().toString().padStart(2, '0')}:00`;
                
                if (
                    isBookingRangeAvailable(
                        bookingData.date,
                        timeString,
                        minBookingDuration,
                        bookingStudioData
                    )
                ) {
                    allSlots.push(timeString);
                }
                
                current.setHours(current.getHours() + 1);
            }
        });

        // Remove duplicates using Set
        const uniqueSlots = [...new Set(allSlots)];
        
        // Sort the time slots from early to late
        uniqueSlots.sort((a, b) => {
            const [hoursA] = a.split(':').map(Number);
            const [hoursB] = b.split(':').map(Number);
            
            // Special case for 24:00
            if (a === "24:00") return 1;  // 24:00 goes last
            if (b === "24:00") return -1; // 24:00 goes last
            
            // Normal hour comparison
            return hoursA - hoursB;
        });

        return uniqueSlots;
    };

    // Improved generateEndTimeSlots from Booking.jsx
    const generateEndTimeSlots = (startTime) => {
        if (!startTime || !bookingData.date) {
            return [];
        }
        
        const timeRanges = getAvailableTimeRanges();

        if (timeRanges.length === 0) {
            return [];
        }
        
        const slots = [];
        // Use the actual selected date for time calculations to handle timezone properly
        const selectedDate = parseLocalDateString(bookingData.date);
        const startDateTime = new Date(`${bookingData.date}T${startTime}`);

        // Find the specific time range that contains the start time
        const startTimeHour = startTime.split(':')[0].padStart(2, '0') + ":00";
        
        const currentRange = timeRanges.find(range => {
            // Convert all times to 24-hour format for proper comparison
            const rangeStart = range.start;
            const rangeEnd = range.end;
            
            // Compare as strings directly for hour-based ranges
            const isStartInRange = 
                // Handle special case where range crosses midnight
                (rangeEnd < rangeStart) ? 
                (startTimeHour >= rangeStart || startTimeHour <= rangeEnd) :
                (startTimeHour >= rangeStart && startTimeHour <= rangeEnd);
            
            return isStartInRange;
        });
        
        if (!currentRange) {
            return [];  // Start time isn't in any available range
        }
        
        // Set endDateTime to the end of the current time range only
        let endDateTime = new Date(selectedDate);
        if (currentRange.end === "00:00" || currentRange.end === "24:00") {
            endDateTime.setHours(24, 0, 0, 0);
        } else {
            const [endHour, endMinute] = currentRange.end.split(':');
            endDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
        }
        
        // Start from minHours after the selected start time
        let current = new Date(startDateTime);
        current.setHours(current.getHours() + minBookingDuration);

        // If minimum booking puts us past the end time, there are no valid end times
        if (current > endDateTime) {
            return [];
        }

        // Find any bookings that could limit our end time
        const busyIntervalsOnSelectedDate = getBusyIntervalItems(bookingStudioData).filter(interval => {
            if (!interval || !interval.startDateTime || !interval.endDateTime) {
                return false;
            }
            
            const bookingDate = new Date(interval.startDateTime).toDateString();
            const ourDate = selectedDate.toDateString();
            return bookingDate === ourDate;
        }) || [];
        
        // Find the earliest booking that starts after our selected start time
        // but only consider bookings within our current availability range
        let earliestNextBooking = null;
        busyIntervalsOnSelectedDate.forEach(interval => {
            const bookingStart = new Date(interval.startDateTime);
            if (bookingStart > startDateTime && bookingStart <= endDateTime) {
                if (!earliestNextBooking || bookingStart < earliestNextBooking) {
                    earliestNextBooking = bookingStart;
                }
            }
        });
        
        // If there's a booking after our start time within our range, limit end times to that booking
        if (earliestNextBooking && earliestNextBooking < endDateTime) {
            endDateTime = earliestNextBooking;
        }

        // Generate all valid end times from minHours after start to the end of the current range
        while (current <= endDateTime) {
            // Always display 24:00 instead of 00:00 for end times
            const timeString = current.getHours() === 0 || current.getHours() === 24 ? "24:00" : 
                             `${current.getHours().toString().padStart(2, '0')}:00`;
            
            // Check if this end time would overlap with any existing bookings
            const wouldOverlap = getBusyIntervalItems(bookingStudioData).some(interval => {
                if (!interval || !interval.startDateTime || !interval.endDateTime) {
                    return false;
                }
                
                const bookingStart = new Date(interval.startDateTime);
                const bookingEnd = new Date(interval.endDateTime);
                
                // End time is invalid if it's within an existing booking
                // Exception: The exact start time of a booking is valid as an end time
                if (current.getTime() !== bookingStart.getTime() && 
                    current > bookingStart && current < bookingEnd) {
                    return true;
                }
                
                return false;
            });

            // Check if this end time would overlap with any blocked times
            const wouldOverlapBlocked = bookingStudioData?.blockedTimes?.items?.some(blocked => {
                if (!blocked || !blocked.startDateTime || !blocked.endDateTime) {
                    return false;
                }
                
                const blockedStart = new Date(blocked.startDateTime);
                const blockedEnd = new Date(blocked.endDateTime);
                
                // End time is invalid if it's within a blocked time
                // Exception: The exact start time of a blocked period is valid as an end time
                if (current.getTime() !== blockedStart.getTime() && 
                    current > blockedStart && current <= blockedEnd) {
                    return true;
                }
                
                return false;
            });
            
            const durationHours = (current - startDateTime) / (1000 * 60 * 60);
            const isRangeAvailable = isBookingRangeAvailable(
                bookingData.date,
                startTime,
                durationHours,
                bookingStudioData
            );

            if (!wouldOverlap && !wouldOverlapBlocked && isRangeAvailable) {
                slots.push(timeString);
            }
            
            current.setHours(current.getHours() + 1);
        }

        return slots;
    };

    const timeSlots = generateTimeSlots();
    const endTimeSlots = generateEndTimeSlots(selectedTime);

    const hasInitiallyUpdatedRef = useRef(false);

    const commitEndTime = (endTime) => {
        setSelectedEndTime(endTime || "");
        updateBookingData('endTime', endTime || "");
    };

    useEffect(() => {
        if (!hasInitiallyUpdatedRef.current && timeSlots.length > 0) {
            // Try to use stored values if they're valid
            let validStartTime = bookingData.startTime;
            if (!timeSlots.includes(validStartTime)) {
                validStartTime = timeSlots[0];
                setSelectedTime(validStartTime);
            }
            
            // Generate valid end times based on the start time
            const validEndTimes = generateEndTimeSlots(validStartTime);
            
            if (validEndTimes.length > 0) {
                let validEndTime = bookingData.endTime;
                if (!validEndTimes.includes(validEndTime)) {
                    validEndTime = validEndTimes[0];
                    setSelectedEndTime(validEndTime);
                }

                updateBookingData('startTime', validStartTime);
                updateBookingData('endTime', validEndTime);
                
                hasInitiallyUpdatedRef.current = true;
            }
        }
    }, [timeSlots]);
    
    const handleStartTimeScroll = () => {
        if (!startScrollerRef.current || timeSlots.length === 0) return;

        // If we're currently snapping programmatically, ignore this scroll event
        if (isStartSnappingRef.current) return;

        const itemHeight = 40;

        // Do not update selectedTime / bookingData on every scroll tick.
        // We only want to commit once the user has stopped scrolling.

        // Clear any existing snap timeout and schedule a new one
        if (startScrollTimeoutRef.current) {
            clearTimeout(startScrollTimeoutRef.current);
        }
        startScrollTimeoutRef.current = setTimeout(() => {
            // Mark that we're snapping so the resulting scroll event is ignored
            isStartSnappingRef.current = true;

            // Snap to the nearest item (iOS-like picker behavior)
            const snapIndex = Math.min(
                Math.max(0, Math.round(startScrollerRef.current.scrollTop / itemHeight)),
                timeSlots.length - 1
            );
            const snapOffset = snapIndex * itemHeight;
            startScrollerRef.current.scrollTop = snapOffset;

            const snapTime = timeSlots[snapIndex];
            if (snapTime && snapTime !== selectedTime) {
                setSelectedTime(snapTime);
                updateBookingData('startTime', snapTime);
                hasSyncedEndScrollRef.current = false;

                const validEndTimes = generateEndTimeSlots(snapTime);
                const currentEndTime =
                    bookingData?.endTime === "00:00" ? "24:00" : bookingData?.endTime;
                if (!validEndTimes.includes(currentEndTime)) {
                    commitEndTime(validEndTimes[0] || "");
                }
            }

            // Allow future scroll events after the snap completes
            // Use a micro-timeout to ensure the scroll event from snapping has fired
            setTimeout(() => {
                isStartSnappingRef.current = false;
            }, 0);
        }, 80); // short delay after user stops scrolling
    };

    const handleEndTimeScroll = () => {
        if (!endScrollerRef.current || endTimeSlots.length === 0) return;

        // If we're currently snapping programmatically, ignore this scroll event
        if (isEndSnappingRef.current) return;

        const itemHeight = 40;

        // As with the start time, do not update on every scroll tick.
        // Wait until scrolling stops, then snap and commit.

        if (endScrollTimeoutRef.current) {
            clearTimeout(endScrollTimeoutRef.current);
        }
        endScrollTimeoutRef.current = setTimeout(() => {
            // Mark that we're snapping so the resulting scroll event is ignored
            isEndSnappingRef.current = true;

            // Snap to the nearest item
            const snapIndex = Math.min(
                Math.max(0, Math.round(endScrollerRef.current.scrollTop / itemHeight)),
                endTimeSlots.length - 1
            );
            const snapOffset = snapIndex * itemHeight;
            endScrollerRef.current.scrollTop = snapOffset;

            const snapTime = endTimeSlots[snapIndex];
            if (snapTime && snapTime !== selectedEndTime) {
                commitEndTime(snapTime);
            }

            // Allow future scroll events after the snap completes
            setTimeout(() => {
                isEndSnappingRef.current = false;
            }, 0);
        }, 80); // short delay after user stops scrolling
    };

    useEffect(() => {
        if (startScrollerRef.current && timeSlots.length > 0) {
            const index = timeSlots.indexOf(selectedTime);
            if (index !== -1) {
                startScrollerRef.current.scrollTop = index * 40;
            } else if (timeSlots.length > 0) {
                // If selected time is not in slots, use first available
                const nextStartTime = timeSlots[0];
                setSelectedTime(nextStartTime);
                updateBookingData('startTime', nextStartTime);
                hasSyncedEndScrollRef.current = false;
            }
        }
    }, [timeSlots]); // Only run when timeSlots changes

    // Update end time when start time changes or new end time slots are available
    useEffect(() => {
        if (endTimeSlots.length === 0) {
            if (selectedEndTime || bookingData?.endTime) {
                commitEndTime("");
            }
            return;
        }

        const storeEndTime =
            bookingData?.endTime === "00:00" ? "24:00" : bookingData?.endTime || "";
        const nextEndTime = endTimeSlots.includes(storeEndTime)
            ? storeEndTime
            : endTimeSlots.includes(selectedEndTime)
                ? selectedEndTime
                : endTimeSlots[0];

        if (nextEndTime !== selectedEndTime) {
            setSelectedEndTime(nextEndTime);
        }

        if (nextEndTime !== storeEndTime) {
            updateBookingData('endTime', nextEndTime);
        }

        if (endTimeSlots.length > 0) {
            if (endScrollerRef.current && !hasSyncedEndScrollRef.current) {
                // Only auto-align the scroll position once on initial mount to avoid visual glitches
                const index = endTimeSlots.indexOf(nextEndTime);
                if (index >= 0) {
                    endScrollerRef.current.scrollTop = index * 40;
                    hasSyncedEndScrollRef.current = true;
                }
            }
        }
    }, [endTimeSlots, selectedEndTime]);

    const formatTimeRange = () => {
        const timeRanges = getAvailableTimeRanges();

        if (timeRanges.length === 0) {
            return "Keine Verfügbarkeit";
        }
        
        if (timeRanges.length === 1 && timeRanges[0].start === "00:00" && timeRanges[0].end === "24:00") {
            return "Ganzer Tag verfügbar";
        }
        
        return timeRanges.map(range => 
            `${range.start} Uhr - ${range.end === "00:00" || range.end === "24:00" ? "24:00" : range.end} Uhr`
        ).join(", ");
    };

    const buttonClickHandler = () => {
        const hasValidStartTime = Boolean(bookingData?.startTime) && timeSlots.includes(bookingData.startTime);
        const storeEndTime =
            bookingData?.endTime === "00:00" ? "24:00" : bookingData?.endTime || "";
        const hasValidEndTime = Boolean(storeEndTime) && endTimeSlots.includes(storeEndTime);

        if (!hasValidStartTime || !hasValidEndTime || timeSlots.length === 0) {
            setErrorMessage("Bitte wähle eine Start- und Endzeit aus.");
        } else {
            nextSlide();
        }
    }

    return (
        <div className={classes.container}>
            <header className={classes.header}>
                <h1 className={`${classes.title} titleSemibold3`}>{formatDate(bookingData.date)}</h1>
            </header>
            <div className={classes.titleContainer}>
                <span className="regularXxs">Verfügbar:</span>
                <span className={`${classes.time} regularXxs`}>{formatTimeRange()}</span>
            </div>
            <div className={classes.timeContainer}>
                <div className={classes.scrollerWrapper}>
                    <div className={classes.scroller} ref={startScrollerRef} onScroll={handleStartTimeScroll}>
                        {timeSlots.length > 0 ? timeSlots.map((time, index) => (
                            <div 
                                key={index}
                                className={classes.timeSlot}
                            >
                                {time}
                            </div>
                        )) : (
                            <div className={classes.timeSlot}>Keine Zeit verfügbar</div>
                        )}
                        <div className={classes.selectionOverlay}>
                            <div className={classes.selectionBox} />
                        </div>
                    </div>
                </div>
                <span className={classes.separator}>bis</span>
                <div className={classes.scrollerWrapper}>
                    <div className={classes.scroller} ref={endScrollerRef} onScroll={handleEndTimeScroll}>
                        {endTimeSlots.length > 0 ? endTimeSlots.map((time, index) => (
                            <div 
                                key={index}
                                className={classes.timeSlot}
                            >
                                {time}
                            </div>
                        )) : (
                            <div className={classes.timeSlot}>Keine Zeit verfügbar</div>
                        )}
                        <div className={classes.selectionOverlay}>
                            <div className={classes.selectionBox} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.weiterButtonContainer}>
                <button 
                    className={classes.weiterButton} 
                    onClick={buttonClickHandler}
                >
                    Weiter
                </button>
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </div>
        </div>
    );
};

export default TimePicker;
